import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuthStore } from '../store/auth';
import { EnvelopeIcon, SparklesIcon, LockClosedIcon } from '@heroicons/react/24/outline';

type Tab = 'magic' | 'password';

export default function LoginPage() {
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();

  const [tab, setTab] = useState<Tab>('magic');

  // Magic link state
  const [magicEmail, setMagicEmail] = useState('');
  const [magicLoading, setMagicLoading] = useState(false);
  const [magicMessage, setMagicMessage] = useState('');
  const [magicError, setMagicError] = useState('');

  // Password login state
  const [pwEmail, setPwEmail] = useState('');
  const [pwPassword, setPwPassword] = useState('');
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError, setPwError] = useState('');

  // ── Magic link handler ────────────────────────────────────────────────────

  const handleMagicSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMagicLoading(true);
    setMagicError('');
    setMagicMessage('');

    try {
      const response = await api.requestMagicLink(magicEmail);
      setMagicMessage(response.message);
      setMagicEmail('');
    } catch (err: any) {
      setMagicError(err.response?.data?.error || 'Failed to send magic link');
    } finally {
      setMagicLoading(false);
    }
  };

  // ── Password login handler ────────────────────────────────────────────────

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwLoading(true);
    setPwError('');

    try {
      const response = await api.adminLogin(pwEmail.trim().toLowerCase(), pwPassword);
      // Clear any stale admin_token so the interceptor uses auth_token only
      localStorage.removeItem('admin_token');
      localStorage.setItem('auth_token', response.token);
      // Build a minimal Client-shaped object so the auth store is happy
      const userAsClient = {
        id: response.user.id,
        business_name: response.user.name,
        business_type: 'admin',
        email: response.user.email,
        current_phase: 0,
        completed_phases: [],
        status: 'active',
        location: '',
        country: '',
        evergreen_connected: false,
        is_admin: true,
        role: response.user.role,
      } as any;
      setAuth(response.token, userAsClient);
      navigate('/admin', { replace: true });
    } catch (err: any) {
      setPwError(err.response?.data?.error || 'Invalid email or password');
    } finally {
      setPwLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl shadow-charcoal-500/5 border border-charcoal-100/60 overflow-hidden">
      {/* Decorative gradient strip at top */}
      <div className="h-1 bg-gradient-to-r from-copper-400 via-sage-400 to-teal-400" />

      <div className="p-8">
        <h2 className="text-2xl font-bold text-charcoal-500 mb-1">Sign in</h2>
        <p className="text-sm text-charcoal-300 mb-6">Access your Trailhead account.</p>

        {/* Tab switcher */}
        <div className="flex rounded-lg border border-charcoal-100 mb-6 overflow-hidden">
          <button
            type="button"
            onClick={() => setTab('magic')}
            className={`flex-1 py-2 text-sm font-medium transition-colors ${
              tab === 'magic'
                ? 'bg-copper-50 text-copper-700 border-r border-charcoal-100'
                : 'text-charcoal-300 hover:text-charcoal-400 border-r border-charcoal-100'
            }`}
          >
            Magic link
          </button>
          <button
            type="button"
            onClick={() => setTab('password')}
            className={`flex-1 py-2 text-sm font-medium transition-colors ${
              tab === 'password'
                ? 'bg-copper-50 text-copper-700'
                : 'text-charcoal-300 hover:text-charcoal-400'
            }`}
          >
            Password
          </button>
        </div>

        {/* ── Magic link form ── */}
        {tab === 'magic' && (
          <form onSubmit={handleMagicSubmit} className="space-y-4">
            <div>
              <label htmlFor="magic-email" className="block text-sm font-medium text-charcoal-400 mb-2">
                Email address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <EnvelopeIcon className="h-5 w-5 text-charcoal-250" />
                </div>
                <input
                  id="magic-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={magicEmail}
                  onChange={(e) => setMagicEmail(e.target.value)}
                  className="input pl-10"
                  placeholder="you@example.com"
                />
              </div>
            </div>

            {magicMessage && (
              <div className="rounded-xl bg-sage-50 p-4 border border-sage-200 flex items-start gap-3">
                <SparklesIcon className="h-5 w-5 text-sage-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-sage-700">{magicMessage}</p>
              </div>
            )}

            {magicError && (
              <div className="rounded-xl bg-red-50 p-4 border border-red-200">
                <p className="text-sm text-red-800">{magicError}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={magicLoading}
              className="btn btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden group"
            >
              <span className="relative z-10">{magicLoading ? 'Sending…' : 'Send magic link'}</span>
            </button>
          </form>
        )}

        {/* ── Password form ── */}
        {tab === 'password' && (
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div>
              <label htmlFor="pw-email" className="block text-sm font-medium text-charcoal-400 mb-2">
                Email address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <EnvelopeIcon className="h-5 w-5 text-charcoal-250" />
                </div>
                <input
                  id="pw-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={pwEmail}
                  onChange={(e) => setPwEmail(e.target.value)}
                  className="input pl-10"
                  placeholder="you@example.com"
                />
              </div>
            </div>

            <div>
              <label htmlFor="pw-password" className="block text-sm font-medium text-charcoal-400 mb-2">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <LockClosedIcon className="h-5 w-5 text-charcoal-250" />
                </div>
                <input
                  id="pw-password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={pwPassword}
                  onChange={(e) => setPwPassword(e.target.value)}
                  className="input pl-10"
                  placeholder="••••••••"
                />
              </div>
            </div>

            {pwError && (
              <div className="rounded-xl bg-red-50 p-4 border border-red-200">
                <p className="text-sm text-red-800">{pwError}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={pwLoading}
              className="btn btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {pwLoading ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        )}

        <div className="flex items-center gap-3 mt-6">
          <div className="flex-1 h-px bg-charcoal-100" />
          <p className="text-xs text-charcoal-250">
            {tab === 'magic' ? 'No password needed' : 'Admin accounts only'}
          </p>
          <div className="flex-1 h-px bg-charcoal-100" />
        </div>
      </div>
    </div>
  );
}
